<div
    class="atl-chatbot-scope"
    style="padding: 0"
    [class]="'view-' + view()"
    [class.embedded]="embeddedPositioning"
    [style.--kendo-color-primary]="primaryColor"
    [style.--kendo-font-size]="fontSize"
>
    <!--
        Anchor for Kendo popups that would otherwise portal to `document.body` by default (only
        the sidebar's row context menu). See `appendTarget`'s doc comment.
        `#appendTargetRef`, not `#appendTarget`: a template reference variable with the same name
        as the `appendTarget` signal below would shadow it for every later binding in this
        template, including the one right here, so `appendTarget()` would silently resolve to this
        element reference instead of the class member and blow up as "not a function".
    -->
    <ng-container #appendTargetRef></ng-container>

    <lib-sidebar
        class="chat-container"
        [(expanded)]="sidebarOpen"
        [sessions]="sessions()"
        [activeClientToken]="identity.clientToken"
        [allowSessionManagement]="canManageSessions"
        [appendTo]="appendTarget()"
        (newChat)="startNewChat()"
        (sessionSelected)="openSession($event)"
        (sessionRenamed)="renameSession($event)"
        (sessionDeleted)="deleteSession($event)"
    >
        <div class="chat-container">
            <div class="chat-header">
                <button kendoButton fillMode="flat" [svgIcon]="hamburgerIcon" [attr.aria-label]="'chatbot.openSidebar' | translate" (click)="sidebarOpen.set(true)"></button>

                <div class="agent-picker">
                    <button
                        type="button"
                        class="agent-picker-trigger"
                        [attr.aria-label]="'chatbot.switchAgent' | translate"
                        [disabled]="!canPickAgent"
                        (click)="canPickAgent && toggleAgentPicker()"
                    >
                        @if (agentsService.isTestSession() && agentsService.agentName; as agentName) {
                            <span>{{ agentDisplayName(agentName) }}</span>
                        } @else {
                            <span>{{ "chatbot.brandName" | translate }}</span>
                        }
                        @if (canPickAgent) {
                            <kendo-svgicon [icon]="chevronDownIcon"></kendo-svgicon>
                        }
                    </button>

                    @if (agentPickerOpen()) {
                        <!-- Click-outside backdrop, same pattern as the view picker and settings panel above. -->
                        <button type="button" class="agent-picker-backdrop" [attr.aria-label]="'chatbot.cancel' | translate" (click)="agentPickerOpen.set(false)"></button>
                        <div class="agent-picker-panel">
                            <div class="agent-picker-title">{{ "chatbot.switchAgentTitle" | translate }}</div>
                            <div class="agent-picker-search">
                                <kendo-svgicon [icon]="searchIcon"></kendo-svgicon>
                                <input
                                    type="text"
                                    [attr.placeholder]="'chatbot.searchAgents' | translate"
                                    [value]="agentSearchQuery()"
                                    (input)="agentSearchQuery.set($any($event.target).value)"
                                />
                            </div>
                            <div class="agent-picker-list">
                                @for (agent of filteredAgents(); track agent.name) {
                                    <button type="button" class="agent-picker-option" (click)="selectAgent(agent)">
                                        {{ agentDisplayName(agent.name) }}
                                    </button>
                                } @empty {
                                    <div class="agent-picker-empty">{{ "chatbot.noAgentsFound" | translate }}</div>
                                }
                            </div>
                        </div>
                    }
                </div>

                <div class="chat-header-spacer"></div>

                @if (showViewPicker()) {
                    <div class="view-picker">
                        <button
                            kendoButton
                            fillMode="flat"
                            [svgIcon]="viewIcons[view()]"
                            [attr.aria-label]="'chatbot.changeView' | translate"
                            (click)="toggleViewPicker()"
                        ></button>

                        @if (viewPickerOpen()) {
                            <!--
                                Click-outside via a full-screen transparent backdrop, same idea as the sidebar's own dialog overlay.
                                A `<button>`, not a `<div>`, so it's focusable and closable without a bespoke keyboard handler.
                            -->
                            <button type="button" class="view-picker-backdrop" [attr.aria-label]="'chatbot.cancel' | translate" (click)="viewPickerOpen.set(false)"></button>
                            <div class="view-picker-panel">
                                <div class="view-picker-title">{{ "chatbot.switchTo" | translate }}</div>
                                @for (option of viewOrder; track option) {
                                    <button type="button" class="view-picker-option" (click)="selectView(option)">
                                        <kendo-svgicon [icon]="viewIcons[option]"></kendo-svgicon>
                                        <span>{{ "chatbot.view." + option | translate }}</span>
                                        @if (option === view()) {
                                            <kendo-svgicon [icon]="checkIcon" class="view-picker-check"></kendo-svgicon>
                                        }
                                    </button>
                                }
                            </div>
                        }
                    </div>
                }

                <div class="settings-picker">
                    <button kendoButton fillMode="flat" [svgIcon]="settingsIcon" [attr.aria-label]="'chatbot.openSettings' | translate" (click)="toggleSettings()"></button>

                    @if (settingsOpen()) {
                        <!-- Click-outside backdrop, same pattern as the view picker above. -->
                        <button type="button" class="settings-backdrop" [attr.aria-label]="'chatbot.cancel' | translate" (click)="settingsOpen.set(false)"></button>
                        <div class="settings-panel">
                            <div class="settings-title">{{ "chatbot.settings" | translate }}</div>
                            <div class="settings-field">
                                <label class="settings-label" for="message-font-size">{{ "chatbot.textSize" | translate }}</label>
                                <kendo-slider
                                    id="message-font-size"
                                    [min]="minMessageFontSize"
                                    [max]="maxMessageFontSize"
                                    [smallStep]="messageFontSizeStep"
                                    tickPlacement="after"
                                    [value]="messageFontSize()"
                                    (valueChange)="onMessageFontSizeChange($event)"
                                ></kendo-slider>
                            </div>

                            @if (identity.testDomain(); as testDomain) {
                                <div class="settings-field">
                                    <button type="button" class="settings-action" (click)="changeTestUser()">
                                        {{ changeIdentityKey | translate: { domain: testDomain } }}
                                    </button>
                                    <div class="settings-hint">{{ changeIdentityHintKey | translate }}</div>
                                </div>
                            }
                        </div>
                    }
                </div>

                @if (view() !== "fullscreen") {
                    <button kendoButton fillMode="flat" [svgIcon]="closeIcon" [attr.aria-label]="'chatbot.closeChat' | translate" (click)="onCloseClick()"></button>
                }
            </div>

            @if (agentsService.isTestSession() && agentsService.agentName; as agentName) {
                <div class="test-mode-banner" role="status">
                    @if (identity.testDomain(); as testDomain) {
                        {{ "chatbot.testModeBannerAs" | translate: { agent: agentDisplayName(agentName), domain: testDomain } }}
                    } @else {
                        {{ "chatbot.testModeBanner" | translate: { agent: agentDisplayName(agentName) } }}
                    }
                </div>
            }

            <div class="chat-body" [style.--chat-message-font-size]="messageFontSize() + 'px'">
                <kendo-chat
                    [authorId]="user.id"
                    [messages]="chatMessages"
                    [(inputValue)]="draftMessage"
                    [placeholder]="'chatbot.chatPlaceholder' | translate"
                    [speechToTextButton]="false"
                    [fileSelectButton]="false"
                    [sendButton]="sendButton"
                    (sendMessage)="sendMessage($event)"
                    class="chat"
                    style="font-size: medium"
                >
                    @if (!canSend) {
                        <!--
                            Replacing the message box template is how Kendo removes the composer. Used while a
                            domain with a credential waits for the host to hand it over.
                        -->
                        <ng-template kendoChatMessageBoxTemplate>
                            @if (agentsService.signInRequired(); as missingDomain) {
                                <div class="composer-notice">
                                    <span>{{ "chatbot.signIn.required" | translate: { domain: missingDomain } }}</span>
                                    <button type="button" class="composer-notice-action" (click)="takeOnIdentityForCurrentAgent()">
                                        {{ takeOnIdentityKey | translate: { domain: missingDomain } }}
                                    </button>
                                </div>
                            } @else {
                                <div class="composer-notice">{{ "chatbot.awaitingCredentials" | translate }}</div>
                            }
                        </ng-template>
                    }

                    <!--
                        Replacing the bubble template is how the Kendo docs render Markdown in a Chat, but it
                        also opts out of everything Kendo draws inside the bubble, including the typing
                        indicator, which lives in its `@if (!useCustomBubbleTemplate)` branch. Hence the
                        `k-chat-bubble` wrapper and the typing branch, both rebuilt by hand here.
                    -->
                    <ng-template kendoChatMessageTemplate let-message>
                        <div
                            #messageElement
                            class="k-chat-bubble k-bubble message-row"
                            [ngClass]="{
                                'from-user': message.author.id === user.id,
                                'from-agent': message.author.id === agent.id,
                            }"
                        >
                            <div class="k-bubble-content message-content">
                                @if (message.typing) {
                                    <div class="k-typing-indicator" [attr.tabindex]="'-1'">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                } @else {
                                    <markdown [data]="message.text"></markdown>
                                }
                            </div>
                        </div>
                        @if (
                            !message.typing && message.author.id === agent.id && message.timestamp === chatMessages[chatMessages.length - 1].timestamp && chatMessages.length > 1
                        ) {
                            <div class="feedback-buttons">
                                <button
                                    kendoButton
                                    [svgIcon]="thumbUpIcon"
                                    aria-label="Thumbs Up"
                                    [ngClass]="{ 'pressed-positive': message.feedbackType === 'positive' }"
                                    (click)="sendFeedback(message.id, 'positive')"
                                ></button>
                                <button
                                    kendoButton
                                    [svgIcon]="thumbDownIcon"
                                    aria-label="Thumbs Down"
                                    [ngClass]="{ 'pressed-negative': message.feedbackType === 'negative' }"
                                    (click)="sendFeedback(message.id, 'negative')"
                                ></button>
                            </div>
                        }
                    </ng-template>
                </kendo-chat>
            </div>
        </div>
    </lib-sidebar>
</div>
